// The notification-channel domain (contract 22 §5 "models/channel.types.ts", §6.2 rows 7-9).
//
// ⚠ CONTRACT DRIFT — §5's `ChannelDescriptor` carries a bare `webhookPrefix?: string`, and both §5
// and §3.3 name the rule "the webhook prefix rule". The rule the legacy actually enforces is TWO
// clauses (`NotificationUpdateModal.vue:173-178`):
//
//   values[0].startsWith(desc[channel].valid) && (channel !== 'teams' || values[0].includes('webhook'))
//
// A Teams URL must ALSO contain the substring `webhook`. `webhookPrefix?: string` cannot express
// that, so a port typed against §5 silently accepts `https://anything` for Teams — the single most
// likely silent regression in this pane. Hence `webhook?: WebhookRule` with an explicit
// `mustContain`, which makes the Teams clause DATA rather than a forgotten `if`.
//
// The read STATE is `SettingsPaneState` (`models/settingsPane.types.ts`); this module mints no twin.

/* ── The channel vocabulary ─────────────────────────────────────────────────── */

export type NotificationChannel = 'email' | 'slack' | 'whatsapp' | 'teams'

/** Where a channel's recipient list lives in the payload (`Notifications.vue:42-67`). Note that
 *  slack and teams SHARE `webhook_urls` — the key is not a function of the channel name. */
export type NotificationValuesKey = 'addresses' | 'webhook_urls' | 'numbers'

export type NotificationPermission =
  'notification_email_only' | 'notification_slack' | 'notification_whatsapp' | 'notification_teams'

/** How the pane validates an entry before it is allowed into the list. */
export type ChannelValidation = 'email' | 'phone' | 'webhook'

/** Both clauses of the webhook rule. `mustContain` exists for Teams and only Teams. */
export interface WebhookRule {
  /** The URL must start with this. */
  prefix: string
  /** …and, when present, must ALSO contain this substring (`teams` → `'webhook'`). */
  mustContain?: string
}

export interface ChannelDescriptor {
  channel: NotificationChannel
  labelKey: string
  valuesKey: NotificationValuesKey
  permission: NotificationPermission
  validation: ChannelValidation
  /** Present iff `validation === 'webhook'`. */
  webhook?: WebhookRule
  /** Email may be enabled with zero recipients; the others may not (`NotificationCard.vue:17`).
   *  ⚠ This governs the toggle's `disabled` ONLY. The "Set up this channel first" tooltip fires on
   *  an empty list for EVERY channel including email (`:13`) — the two rules are not the same rule,
   *  and reading §14 as though both carry the exception drops the email tooltip. */
  enableableWhenEmpty: boolean
  /** How many numbered `…setup.<channel>.stepN` lines the modal's info panel renders. Layout, not
   *  copy: the legacy renders an ordered LIST above 1 and a single paragraph at 1
   *  (`NotificationUpdateModal.vue:75-81`). */
  setupSteps: number
}

/** `Notifications.vue:42-67`, verbatim — the channel→payload-key map the ticket pins. */
export const CHANNEL_CATALOG: readonly ChannelDescriptor[] = [
  {
    channel: 'email',
    labelKey: 'workspaceSettings.notifications.channel.email.label',
    valuesKey: 'addresses',
    permission: 'notification_email_only',
    validation: 'email',
    enableableWhenEmpty: true,
    setupSteps: 1,
  },
  {
    channel: 'slack',
    labelKey: 'workspaceSettings.notifications.channel.slack.label',
    valuesKey: 'webhook_urls',
    permission: 'notification_slack',
    validation: 'webhook',
    webhook: { prefix: 'https://hooks.slack.com/' },
    enableableWhenEmpty: false,
    setupSteps: 4,
  },
  {
    channel: 'whatsapp',
    labelKey: 'workspaceSettings.notifications.channel.whatsapp.label',
    valuesKey: 'numbers',
    permission: 'notification_whatsapp',
    validation: 'phone',
    enableableWhenEmpty: false,
    setupSteps: 1,
  },
  {
    channel: 'teams',
    labelKey: 'workspaceSettings.notifications.channel.teams.label',
    valuesKey: 'webhook_urls',
    permission: 'notification_teams',
    validation: 'webhook',
    // ⚠ THE clause a port drops: prefix AND `webhook` in the URL (`:173-178`).
    webhook: { prefix: 'https://', mustContain: 'webhook' },
    enableableWhenEmpty: false,
    setupSteps: 4,
  },
]

/* ── Domain read model ──────────────────────────────────────────────────────── */

export interface ChannelConfig {
  enabled: boolean
  values: string[]
}

/** What 13 and 17 read: a channel is selectable iff configured AND enabled (17 §2). */
export interface ChannelAvailability {
  channel: NotificationChannel
  configured: boolean
  enabled: boolean
  recipients: string[]
}

/* ── Wire DTOs ──────────────────────────────────────────────────────────────── */

/** One channel's block inside `channels`. The `google_allowed_*` arrays are NOT this pane's — they
 *  belong to the Google-Sheet permissions surface (ORP-19876), which reads them off the same GET
 *  (`UsersPermissions.vue:611-614`). They are typed here so that consumer does not have to fork the
 *  api class to see them. */
export interface NotificationChannelDto {
  enabled?: boolean
  addresses?: string[]
  webhook_urls?: string[]
  numbers?: string[]
  google_allowed_domains?: string[]
  google_allowed_emails?: string[]
  [key: string]: unknown
}

/**
 * The WHOLE body of `GET /notifications/information/{w}/all/`.
 *
 * ⚠ Bare — there is no `{data:…}` envelope (BE-22-07), and the body carries keys OUTSIDE
 * `channels`: `is_token_owner` gates ORP-19876's modal (`UsersPermissions.vue:610`). An api that
 * returned only `.channels` would drop them silently, so the api returns this whole DTO.
 */
export interface NotificationInformationDto {
  channels?: Partial<Record<NotificationChannel, NotificationChannelDto>>
  /** ORP-19876 (`UsersPermissions.vue:610`) — never read by this pane, never dropped by its api. */
  is_token_owner?: boolean
  [key: string]: unknown
}

/** The `400` body of `PUT …/update/` (contract §6.2 row 9, BE-22-03). Rendered as a STRUCTURED
 *  list — see `service/channelValidation.ts#invalidRecipients`. */
export interface InvalidRecipientsDto {
  message?: string
  invalid_emails?: unknown
}
